import { useContext, useEffect, useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Breadcrumbs, Pagination, Typography, Box, FormControl, Select, MenuItem, InputLabel, FormHelperText, Tabs, Tab, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper } from '@mui/material';
import { MainContainer, Container } from '../../../../components';
import { Menu } from '../Menu/Menu';

import { getDataForScoreManagement } from '../../../../services/masterService';
import { getAttendanceDataReport, downloadAttendanceDataReport, getCandidateResultReport, downloadCandidateResultReport, getCandidateResponseSheet, downloadCandidateResponseSheet } from '../../../../services/reportsService';

import ARROW from '../../../../assets/images/breadcrumb-arrow.svg';
import ARROW_OPEN from '../../../../assets/images/breadcrumb-arrow-open.svg';
import SEARCH_ICON from '../../../../assets/images/search-icon.svg';
import DOWNLOAD from '../../../../assets/images/download-white.svg';
import DOWNLOAD_GRAY from '../../../../assets/images/download-gray.svg';
import RadiusButton from '../../../../components/RadiusButton';

import dayjs from '../../../../config/dayjs';
import { useToaster } from '../../../../providers/ToasterProvider';
import { ProjectContext } from '../../../../providers/projectProvider';

function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`result-tabpanel-${index}`}
            aria-labelledby={`result-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `result-tab-${index}`,
        'aria-controls': `result-tabpanel-${index}`,
    };
}

let timer = null;
const limit = parseInt(process.env.REACT_APP_PAGE_LIMIT);

export const Reports = () => {

    const { addNotification } = useToaster();
    const { projectDetails } = useContext(ProjectContext);
    const defaultDrive = { id: 0, driveName: 'None' };
    const [drives, setDrives] = useState([defaultDrive]);
    const [selectedDrive, setSelectedDrive] = useState(0);
    const defaultCourse = { id: 0, courseName: 'None' };
    const [allCourses, setAllCourses] = useState([]);
    const [courses, setCourses] = useState([defaultCourse]);
    const [selectedCourse, setSelectedCourse] = useState(0);
    const defaultBatch = { id: 0, batchName: 'None' };
    const [allBatches, setAllBatches] = useState([defaultBatch]);
    const [batches, setBatches] = useState([defaultBatch]);
    const [selectedBatch, setSelectedBatch] = useState(0);
    const defaultProjectMaster = { id: 0, driveId: 0, courseId: 0, batchId: 0, displayExamDate: 'None' };
    const [projectMasters, setProjectMasters] = useState([defaultProjectMaster]);
    const [selectedProjectMaster, setSelectedProjectMaster] = useState(0);
    const [filteredProjectMasters, setFilteredProjectMasters] = useState([defaultProjectMaster]);
    const [selectedTab, setSelectedTab] = useState(0);

    const [maxPages, setMaxPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [search, setSearch] = useState('');
    const [newSearch, setNewSearch] = useState(false);

    const [attendanceDataColumns, setAttendanceDataColumns] = useState([]);
    const [attendanceReportData, setAttendanceReportData] = useState([]);
    const [candidateResultDataColumns, setCandidateResultDataColumns] = useState([]);
    const [candidateResultReportData, setCandidateResultReportData] = useState([]);
    const [candidateResponseSheetDataColumns, setCandidateResponseSheetDataColumns] = useState([]);
    const [candidateResponseSheetData, setCandidateResponseSheetData] = useState([]);

    const reportsSchema = z.object({
        drive: z.number().gte(1),
        course: z.number().gte(1),
        batch: z.number().gte(1),
        examDate: z.number().gte(1),
    });

    const {
        formState: { errors },
        trigger,
        setValue,
    } = useForm({
        resolver: zodResolver(reportsSchema),
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await getDataForScoreManagement();
                if (response.code === 'OK') {
                    setDrives([defaultDrive, ...response.data.drives]);
                    setAllCourses([...response.data.courses]);
                    setAllBatches([...response.data.batches]);
                    setProjectMasters(response.data.projectMasters.map(pm => {
                        pm.displayExamDate = dayjs(pm.examDate).format('DD-MM-YYYY')
                        return pm;
                    }));
                }
            } catch (error) {
                console.log('There is an error while fetching update time required data', error);
            }
        }
        fetchData();
    }, []);

    useEffect(() => {
        const filteredCourses = [];
        if (selectedDrive) {
            for (let pm of projectMasters) {
                if (pm.driveId === selectedDrive) {
                    const course = allCourses.find((course) => course.id === pm.courseId);
                    if (course && !filteredCourses.some((c) => c.id === course.id)) {
                        filteredCourses.push(course);
                    }
                }
            }
        }
        setCourses([defaultCourse, ...filteredCourses]);
        setSelectedCourse(0);
        setValue('course', 0);

        setBatches([defaultBatch]);
        setSelectedBatch(0);
        setValue('batch', 0);

        setFilteredProjectMasters([defaultProjectMaster]);
        setSelectedProjectMaster(0);
        setValue('examDate', 0);
    }, [selectedDrive]);

    useEffect(() => {
        const filteredBatches = [];
        if (selectedCourse) {
            for (let pm of projectMasters) {
                if (pm.driveId === selectedDrive && pm.courseId === selectedCourse) {
                    const batch = allBatches.find((batch) => batch.id === pm.batchId);
                    if (batch && !filteredBatches.some((c) => c.id === batch.id)) {
                        filteredBatches.push(batch);
                    }
                }
            }
        }

        setBatches([defaultBatch, ...filteredBatches]);
        setSelectedBatch(0);
        setValue('batch', 0);

        setFilteredProjectMasters([defaultProjectMaster]);
        setSelectedProjectMaster(0);
        setValue('examDate', 0);
    }, [selectedCourse]);

    useEffect(() => {
        if (selectedDrive && selectedCourse && selectedBatch) {
            const pms = projectMasters.filter((pm) => pm.driveId === selectedDrive && pm.courseId === selectedCourse && pm.batchId === selectedBatch);
            setFilteredProjectMasters([defaultProjectMaster, ...pms]);
        } else {
            setFilteredProjectMasters([defaultProjectMaster]);
            setSelectedProjectMaster(0);
        }
    }, [selectedDrive, selectedCourse, selectedBatch]);

    useEffect(() => {
        handleSelectProjectMaster();
    }, [currentPage, newSearch]);

    const onSearch = (e) => {
        setSearch(e.target.value);
        if (timer) {
            clearTimeout(timer);
        }
        timer = setTimeout(() => {
            setCurrentPage(1);
            setNewSearch(!newSearch);
        }, 400);
    }
    const handlePageChange = (e, value) => {
        setCurrentPage(value)
    };

    const handleSelectProjectMaster = async () => {
        if (!selectedProjectMaster) return;
        try {
            if (selectedTab === 0) {
                const body = {
                    projectMasterId: selectedProjectMaster,
                    page: currentPage,
                    search: search,
                    limit: limit
                }
                const response = await getAttendanceDataReport(body);
                if (response.code === "OK") {
                    const count = response.data.count ?? 0;
                    setMaxPages(Math.ceil(count / limit));
                    const attendanceReportData = response?.data?.rows ?? [];
                    setAttendanceDataColumns([
                        { id: 'srNo', label: 'Sr. No.', minWidth: 120 },
                        { id: 'rollNo', label: "Candidate's Roll No", minWidth: 140 },
                        { id: 'applicationNo', label: "Candidate's Application  No", minWidth: 140 },
                        { id: 'candidateName', label: 'Candidate Name', minWidth: 160 },
                        { id: 'courseName', label: 'Exam/Post Name', minWidth: 160 },
                        { id: 'examDate', label: 'Exam Date', minWidth: 140 },
                        { id: 'hallName', label: 'Venue Name', minWidth: 140 },
                        { id: 'venueId', label: 'Venue Code', minWidth: 140 },
                        { id: 'batchName', label: 'Shift No.', minWidth: 140 },
                        { id: 'batchTime', label: 'Shift Timing', minWidth: 220 },
                        { id: 'hasAttended', label: 'Attendance Status', minWidth: 120 },
                        { id: 'loginTime', label: 'Login Time', minWidth: 180 },
                        { id: 'logoutTime', label: 'Logout Time', minWidth: 180 },
                    ]);
                    setAttendanceReportData(attendanceReportData.map((candidate) => {
                        if (candidate.loginTime) {
                            candidate.loginTime = dayjs.utc(candidate['loginTime']).format('DD-MM-YYYY hh:mm:ss a');
                        }
                        if (candidate.logoutTime) {
                            candidate.logoutTime = dayjs.utc(candidate['logoutTime']).format('DD-MM-YYYY hh:mm:ss a');
                        }
                        candidate.startTime = dayjs.utc(candidate['startTime']).format('DD-MM-YYYY hh:mm:ss a');
                        candidate.endTime = dayjs.utc(candidate['endTime']).format('DD-MM-YYYY hh:mm:ss a');
                        return candidate;
                    }));
                }
            } else if (selectedTab === 1) {
                const body = {
                    projectMasterId: selectedProjectMaster,
                    page: currentPage,
                    search: search,
                    limit: limit
                }
                const response = await getCandidateResultReport(body);
                if (response.code === "OK") {
                    const count = response.data.count ?? 0;
                    setMaxPages(Math.ceil(count / limit));
                    const columns = [
                        { id: 'srNo', label: 'Sr. No.', minWidth: 120 },
                        { id: 'rollNo', label: "Candidate's Roll No", minWidth: 140 },
                        { id: 'applicationNo', label: "Candidate's Application  No", minWidth: 140 },
                        { id: 'candidateName', label: 'Candidate Name', minWidth: 160 },
                        { id: 'courseName', label: 'Exam/Post Name', minWidth: 160 },
                        { id: 'examDate', label: 'Exam Date', minWidth: 140 },
                        { id: 'hallName', label: 'Venue Name', minWidth: 140 },
                        { id: 'venueId', label: 'Venue Code', minWidth: 140 },
                    ];
                    const allSections = response.data.sections.map((section) => ({
                        ...section,
                        sectionTitleLowerCase: section.sectionTitle.toLowerCase()
                    }));
                    for (let section of allSections) {
                        columns.push({
                            id: `${section.sectionTitleLowerCase}.totalQuestions`,
                            label: `${section.sectionTitle === 'default' ? '' : section.sectionTitle} Total number of questions`,
                            minWidth: 140
                        });
                        columns.push({
                            id: `${section.sectionTitleLowerCase}.attemptedQuestions`,
                            label: `${section.sectionTitle === 'default' ? '' : section.sectionTitle} Total attempted`,
                            minWidth: 140
                        });
                        columns.push({
                            id: `${section.sectionTitleLowerCase}.notAttemptedQuestions`,
                            label: `${section.sectionTitle === 'default' ? '' : section.sectionTitle} Total not attempted`,
                            minWidth: 140
                        });
                        columns.push({
                            id: `${section.sectionTitleLowerCase}.correctQuestions`,
                            label: `${section.sectionTitle === 'default' ? '' : section.sectionTitle} Total correct questions`,
                            minWidth: 140
                        });
                        columns.push({
                            id: `${section.sectionTitleLowerCase}.incorrectQuestions`,
                            label: `${section.sectionTitle === 'default' ? '' : section.sectionTitle} Total incorrect questions`,
                            minWidth: 140
                        });
                    }
                    setCandidateResultDataColumns(columns);

                    const candidateResultReport = response?.data?.rows ?? [];
                    setCandidateResultReportData(candidateResultReport);
                }
            } else if (selectedTab === 2) {
                const body = {
                    projectMasterId: selectedProjectMaster,
                    page: currentPage,
                    search: search,
                    limit: limit
                }
                const response = await getCandidateResponseSheet(body);
                if (response.code === "OK") {
                    const count = response.data.count ?? 0;
                    setMaxPages(Math.ceil(count / limit));
                    const columns = [
                        { id: 'srNo', label: 'Sr. No.', minWidth: 120 },
                        { id: 'rollNo', label: "Candidate's Roll No", minWidth: 140 },
                        { id: 'candidateName', label: 'Candidate Name', minWidth: 160 },
                        { id: 'courseName', label: 'Exam/Post Name', minWidth: 160 },
                        { id: 'hallName', label: 'Venue Name', minWidth: 140 },
                        { id: 'usedTime', label: 'Used Duration', minWidth: 140 },
                        { id: 'sectionName', label: 'Section Name', minWidth: 140 },
                        { id: 'questionNo', label: 'Candidate Question No.', minWidth: 140 },
                        { id: 'qbQuestionNo', label: 'QB Question No.', minWidth: 140 },
                        { id: 'questionId', label: 'Question ID', minWidth: 140 },
                        { id: 'selectedOption', label: 'Selected Option', minWidth: 140 },
                        { id: 'selectedOptionId', label: 'Selected Option ID', minWidth: 140 },
                        { id: 'correctOption', label: 'Correct Option', minWidth: 140 },
                        { id: 'correctOptionId', label: 'Correct Option ID', minWidth: 140 },
                        { id: 'timestamp', label: 'Timestamp', minWidth: 140 },
                    ];

                    const candidate = response?.data?.rows?.[0] ?? {};
                    setCandidateResponseSheetDataColumns(columns);
                    setCandidateResponseSheetData(candidate.questionDetails.map((question) => {
                        return {
                            ...question,
                            timestamp: question.timestamp ? dayjs.utc(question.timestamp) : null,
                            rollNo: candidate.rollNo,
                            candidateName: candidate.candidateName,
                            courseName: candidate.courseName,
                            hallName: candidate.hallName,
                            usedTime: candidate.usedTime,
                        };
                    }));
                }
            }

        } catch (error) {
            const code = error?.response?.data?.code;
            if (code === 'EXAM_NOT_ENDED') {
                addNotification('Exam not ended.', 'information');
            }
            console.log('There is an error while getting present count', error);
        }
    }

    const handleDownloadReport = async () => {
        if (!selectedProjectMaster) return;
        try {
            console.log('selecting project master selected tab is', selectedTab);
            if (selectedTab === 0) {
                const body = {
                    projectMasterId: selectedProjectMaster,
                    search: search,
                }
                const res = await downloadAttendanceDataReport(body);
                const url = window.URL.createObjectURL(res);

                const link = document.createElement('a');
                link.href = url;
                link.setAttribute(
                    'download',
                    `Attendance Data.xlsx`,
                );
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                addNotification('File downloaded successfully.', 'success');

            } else if (selectedTab === 1) {
                const body = {
                    projectMasterId: selectedProjectMaster,
                    search: search,
                }
                const res = await downloadCandidateResultReport(body);
                const url = window.URL.createObjectURL(res);

                const link = document.createElement('a');
                link.href = url;
                link.setAttribute(
                    'download',
                    `Candidate Result Report.xlsx`,
                );
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                addNotification('File downloaded successfully.', 'success');
            } else if (selectedTab === 2) {
                const body = {
                    projectMasterId: selectedProjectMaster,
                    search: search,
                }
                const res = await downloadCandidateResponseSheet(body);
                const url = window.URL.createObjectURL(res);

                const link = document.createElement('a');
                link.href = url;
                link.setAttribute(
                    'download',
                    `Candidate Response Sheet.xlsx`,
                );
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                addNotification('File downloaded successfully.', 'success');
            }

        } catch (error) {
            const code = error?.response?.data?.code;
            console.log('There is an error while downloading report', error);
        }
    }

    const handleTabChange = (newValue) => {
        setSelectedTab(newValue);
        setMaxPages(1);
        setCurrentPage(1);
        setSearch('');
        setAttendanceDataColumns([]);
        setAttendanceReportData([]);
        setCandidateResultDataColumns([]);
        setCandidateResultReportData([]);
        setCandidateResponseSheetDataColumns([]);
        setCandidateResponseSheetData([]);

        setNewSearch(!newSearch);
    }

    const renderAttendanceReportTableCell = (columnId, rowIndex) => {
        const row = attendanceReportData[rowIndex];
        switch (columnId) {
            case 'srNo':
                return rowIndex + 1 + ((currentPage - 1) * limit);
            case 'batchTime':
                return <>
                    <Box component='span'>
                        {row['startTime']}
                    </Box>
                    <br />
                    <Box component='span'>
                        to
                    </Box>
                    <br />
                    <Box component='span'>
                        {row['endTime']}
                    </Box>
                </>
            case 'hasAttended':
                return <Box component='span' sx={{
                    cursor: 'default',
                    color: row?.['hasAttended'] ? 'success.main' : 'error.main'
                }}>
                    {row?.['hasAttended'] ? 'Yes' : 'No'}
                </Box>
            case 'examDate':
                return dayjs(row['examDate']).format('DD-MM-YYYY')
            case 'candidateCount':
            default:
                return row[columnId];
        }
    }

    const renderCandidateResultReportTableCell = (columnId, rowIndex) => {
        const row = candidateResultReportData[rowIndex];
        switch (true) {
            case columnId === 'srNo':
                return rowIndex + 1 + ((currentPage - 1) * limit);
            case columnId === 'examDate':
                return dayjs(row['examDate']).format('DD-MM-YYYY')
            case columnId.includes('.totalQuestions'):
                const totalQuestionsSectionName = columnId.split('.')[0];
                return row['sectionWiseQuestionStates'][totalQuestionsSectionName]?.totalQuestions ?? 0;
            case columnId.includes('.attemptedQuestions'):
                const attemptedQuestionsSectionName = columnId.split('.')[0];
                return row['sectionWiseQuestionStates'][attemptedQuestionsSectionName]?.attemptedQuestions ?? 0;
            case columnId.includes('.notAttemptedQuestions'):
                const notAttemptedQuestionsSectionName = columnId.split('.')[0];
                return row['sectionWiseQuestionStates'][notAttemptedQuestionsSectionName]?.notAttemptedQuestions ?? 0;
            case columnId.includes('.correctQuestions'):
                const correctQuestionsSectionName = columnId.split('.')[0];
                return row['sectionWiseQuestionStates'][correctQuestionsSectionName]?.correctQuestions ?? 0;
            case columnId.includes('.incorrectQuestions'):
                const incorrectQuestionsSectionName = columnId.split('.')[0];
                return row['sectionWiseQuestionStates'][incorrectQuestionsSectionName]?.incorrectQuestions ?? 0;
            default:
                return row[columnId];
        }
    }

    const renderCandidateResponseSheetTableCell = (columnId, rowIndex) => {
        const row = candidateResponseSheetData[rowIndex];
        switch (columnId) {
            case 'srNo':
                return rowIndex + 1 + ((currentPage - 1) * limit);
            case 'timestamp':
                return row['timestamp'] ? dayjs(row['timestamp']).format('DD-MM-YYYY hh:mm:ss a') : null;
            case 'candidateCount':
            default:
                return row[columnId];
        }
    }

    return (
        <MainContainer>
            <Menu>
                <Breadcrumbs aria-label="breadcrumb" separator='' sx={{ gap: 12 }}>
                    <Typography color="text.primary" sx={{ fontWeight: 400, textTransform: 'capitalize' }}>{projectDetails?.projectName}</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW} alt=''></img>
                    </Box>
                    <Typography color="text.primary" sx={{ fontWeight: 500 }}>
                        Reports
                    </Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW_OPEN} alt='' ></img>
                    </Box>
                </Breadcrumbs>
            </Menu>

            <Container>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: 'calc(100vh - 116px)',
                }}>
                    <Typography component='h5' variant='h5' sx={{ mb: 2 }}>Reports</Typography>
                    <Box sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'flex-start',
                        marginBottom: 3,
                        columnGap: 2,
                        rowGap: 1
                    }}>
                        <FormControl className='form-field' sx={{ width: 200 }}>
                            <InputLabel id="driveName">Drive Name</InputLabel>
                            <Select
                                size='small'
                                labelId="driveName"
                                id="drive"
                                label='Drive Name'
                                name='drive'
                                error={Boolean(errors.drive)}
                                onBlur={() => trigger('drive')}
                                value={selectedDrive ?? 0}
                                onChange={(e, event) => {
                                    const drive = drives.find((d) => d.id === event.props.value);
                                    setSelectedDrive(drive.id);
                                    setValue('drive', event.props.value);
                                }}
                            >
                                {
                                    drives.map(
                                        (drive) => <MenuItem key={drive.id} value={drive.id}>
                                            {drive.driveName}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {errors.drive?.message
                                && <FormHelperText sx={{ color: 'error.main' }}>Please select drive name.</FormHelperText>
                            }
                        </FormControl>
                        <FormControl className='form-field' sx={{ width: 200 }}>
                            <InputLabel id="courseName">Course Name</InputLabel>
                            <Select
                                size='small'
                                labelId="courseName"
                                id="course"
                                label='Course Name'
                                name='course'
                                error={Boolean(errors.course)}
                                onBlur={() => trigger('course')}
                                value={selectedCourse ?? 0}
                                onChange={(e, event) => {
                                    const course = courses.find((d) => d.id === event.props.value);
                                    setSelectedCourse(course.id);
                                    setValue('course', event.props.value);
                                }}
                            >
                                {
                                    courses.map(
                                        (course) => <MenuItem key={course.id} value={course.id}>
                                            {course.courseName}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {errors.course?.message
                                && <FormHelperText sx={{ color: 'error.main' }}>Please select course name.</FormHelperText>
                            }
                        </FormControl>
                        <FormControl className='form-field' sx={{ width: 200 }}>
                            <InputLabel id="batchName">Batch Name</InputLabel>
                            <Select
                                size='small'
                                labelId="batchName"
                                id="batch"
                                label='Batch Name'
                                name='batch'
                                error={Boolean(errors.batch)}
                                onBlur={() => trigger('batch')}
                                value={selectedBatch ?? 0}
                                onChange={(e, event) => {
                                    const batch = batches.find((b) => b.id === event.props.value);
                                    setSelectedBatch(batch.id);
                                    setValue('batch', event.props.value);
                                }}
                            >
                                {
                                    batches.map(
                                        (batch) => <MenuItem key={batch.id} value={batch.id}>
                                            {batch.batchName}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {errors.batch?.message
                                && <FormHelperText sx={{ color: 'error.main' }}>Please select batch name.</FormHelperText>
                            }
                        </FormControl>
                        <FormControl className='form-field' sx={{ width: 200 }}>
                            <InputLabel id="examDateLabel">Exam Date</InputLabel>
                            <Select
                                size='small'
                                labelId="examDateLabel"
                                id="examDate"
                                label='Exam Date'
                                name='examDate'
                                error={Boolean(errors.examDate)}
                                onBlur={() => trigger('examDate')}
                                value={selectedProjectMaster ?? 0}
                                onChange={(e, event) => {
                                    const projectMaster = filteredProjectMasters.find((pm) => pm.id === event.props.value);
                                    setSelectedProjectMaster(projectMaster.id);
                                    setValue('examDate', event.props.value);
                                }}
                            >
                                {
                                    filteredProjectMasters.map(
                                        (pm) => <MenuItem key={pm.id} value={pm.id}>
                                            {pm.displayExamDate}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {errors.examDate?.message
                                && <FormHelperText sx={{ color: 'error.main' }}>Please select exam date.</FormHelperText>
                            }
                        </FormControl>

                        <RadiusButton sx={{
                            textTransform: 'uppercase',
                            fontWeight: 500,
                            ml: 'auto'
                        }} color='primary'
                            variant='contained'
                            size='large'
                            onClick={handleSelectProjectMaster}
                            disabled={!selectedDrive || !selectedCourse || !selectedBatch || !selectedProjectMaster}
                        >
                            Select
                        </RadiusButton>
                    </Box>
                    <Box sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: 'background.base',
                        p: 0
                    }}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={selectedTab} onChange={(e, v) => handleTabChange(v)} aria-label="basic tabs example">
                                <Tab label="Attendance Data" {...a11yProps(0)} />
                                <Tab label="Candidate Result Report" {...a11yProps(1)} />
                                <Tab label="Candidate Response Sheet" {...a11yProps(2)} />
                            </Tabs>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'space-between', mt: 1, ml: 1.5 }}>
                            <div className='search-wrapper'>
                                <input type='text' value={search} onChange={onSearch} className='search-box' placeholder='Search...' />
                                <img className='search-icon' src={SEARCH_ICON} alt='' />
                            </div>
                            <RadiusButton sx={{
                                textTransform: 'uppercase',
                                fontWeight: 500,
                            }} color="primary"
                                type="submit"
                                variant='contained'
                                startIcon={!selectedDrive || !selectedCourse || !selectedBatch || !selectedProjectMaster ? <img src={DOWNLOAD_GRAY} alt='' /> : <img src={DOWNLOAD} alt='' />}
                                onClick={handleDownloadReport}
                                disabled={!selectedDrive || !selectedCourse || !selectedBatch || !selectedProjectMaster}
                                size='large'>
                                Download
                            </RadiusButton>
                        </Box>
                        <CustomTabPanel value={selectedTab} index={0}>
                            <Paper sx={{
                                width: '100%',
                                overflow: 'hidden',
                                bgcolor: 'background.base',
                                maxHeight: 'calc(100vh - 390px)',
                                maxWidth: 'calc(100vw - 170px)',
                                borderRadius: 1,
                            }}>
                                <TableContainer sx={{
                                    px: 1,
                                    pb: 1,
                                    maxHeight: 'inherit',
                                    maxWidth: 'inherit',
                                    border: '1px solid',
                                    borderColor: 'primary.main',
                                    borderRadius: 1
                                }}>
                                    <Table stickyHeader aria-label="sticky table">
                                        <TableHead>
                                            <TableRow>
                                                {attendanceDataColumns.map((column) => (
                                                    <TableCell
                                                        sx={{
                                                            color: 'primary.main'
                                                        }}
                                                        key={column.id}
                                                        align='center'
                                                        style={{ minWidth: column.minWidth }}
                                                    >
                                                        {column.label}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {!attendanceReportData?.length
                                                ? <TableRow sx={{ mx: 'auto', p: 2, color: 'grey.500' }}>
                                                    <TableCell colSpan={attendanceDataColumns.length} sx={{ border: 'none', textAlign: 'center' }}>
                                                        No records available
                                                    </TableCell>
                                                </TableRow>
                                                : attendanceReportData.map((row, rowIndex) => {
                                                    return (
                                                        <TableRow hover={false} key={rowIndex}>
                                                            {attendanceDataColumns.map((column, index) => {
                                                                return <TableCell key={column.id} align='center' sx={{
                                                                    px: 1.5,
                                                                    py: 0.75
                                                                }}>
                                                                    {renderAttendanceReportTableCell(column.id, rowIndex)}
                                                                </TableCell>
                                                            })}
                                                        </TableRow>
                                                    );
                                                })
                                            }
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </CustomTabPanel>
                        <CustomTabPanel value={selectedTab} index={1}>
                            <Paper sx={{
                                width: '100%',
                                overflow: 'hidden',
                                bgcolor: 'background.base',
                                maxHeight: 'calc(100vh - 390px)',
                                maxWidth: 'calc(100vw - 170px)',
                                borderRadius: 1,
                            }}>
                                <TableContainer sx={{
                                    px: 1,
                                    pb: 1,
                                    maxHeight: 'inherit',
                                    maxWidth: 'inherit',
                                    border: '1px solid',
                                    borderColor: 'primary.main',
                                    borderRadius: 1
                                }}>
                                    <Table stickyHeader aria-label="sticky table">
                                        <TableHead>
                                            <TableRow>
                                                {candidateResultDataColumns.map((column) => (
                                                    <TableCell
                                                        sx={{
                                                            color: 'primary.main'
                                                        }}
                                                        key={column.id}
                                                        align='center'
                                                        style={{ minWidth: column.minWidth }}
                                                    >
                                                        {column.label}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {!candidateResultReportData?.length
                                                ? <TableRow sx={{ mx: 'auto', p: 2, color: 'grey.500' }}>
                                                    <TableCell colSpan={candidateResultDataColumns.length} sx={{ border: 'none', textAlign: 'center' }}>
                                                        No records available
                                                    </TableCell>
                                                </TableRow>
                                                : candidateResultReportData.map((row, rowIndex) => {
                                                    return (
                                                        <TableRow hover={false} key={rowIndex}>
                                                            {candidateResultDataColumns.map((column, index) => {
                                                                return <TableCell key={column.id} align='center' sx={{
                                                                    px: 1.5,
                                                                    py: 0.75
                                                                }}>
                                                                    {renderCandidateResultReportTableCell(column.id, rowIndex)}
                                                                </TableCell>
                                                            })}
                                                        </TableRow>
                                                    );
                                                })
                                            }
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </CustomTabPanel>
                        <CustomTabPanel value={selectedTab} index={2}>
                            <Paper sx={{
                                width: '100%',
                                overflow: 'hidden',
                                bgcolor: 'background.base',
                                maxHeight: 'calc(100vh - 390px)',
                                maxWidth: 'calc(100vw - 170px)',
                                borderRadius: 1,
                            }}>
                                <TableContainer sx={{
                                    px: 1,
                                    pb: 1,
                                    maxHeight: 'inherit',
                                    maxWidth: 'inherit',
                                    border: '1px solid',
                                    borderColor: 'primary.main',
                                    borderRadius: 1
                                }}>
                                    <Table stickyHeader aria-label="sticky table">
                                        <TableHead>
                                            <TableRow>
                                                {candidateResponseSheetDataColumns.map((column) => (
                                                    <TableCell
                                                        sx={{
                                                            color: 'primary.main'
                                                        }}
                                                        key={column.id}
                                                        align='center'
                                                        style={{ minWidth: column.minWidth }}
                                                    >
                                                        {column.label}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {!candidateResponseSheetData?.length
                                                ? <TableRow sx={{ mx: 'auto', p: 2, color: 'grey.500' }}>
                                                    <TableCell colSpan={candidateResponseSheetDataColumns.length} sx={{ border: 'none', textAlign: 'center' }}>
                                                        No records available
                                                    </TableCell>
                                                </TableRow>
                                                : candidateResponseSheetData.map((row, rowIndex) => {
                                                    return (
                                                        <TableRow hover={false} key={rowIndex}>
                                                            {candidateResponseSheetDataColumns.map((column, index) => {
                                                                return <TableCell key={column.id} align='center' sx={{
                                                                    px: 1.5,
                                                                    py: 0.75
                                                                }}>
                                                                    {renderCandidateResponseSheetTableCell(column.id, rowIndex)}
                                                                </TableCell>
                                                            })}
                                                        </TableRow>
                                                    );
                                                })
                                            }
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </CustomTabPanel>
                        <Pagination sx={{ mt: 'auto' }} count={maxPages} page={currentPage} onChange={handlePageChange} shape="rounded" />
                    </Box>
                </Box>
            </Container>

        </MainContainer >
    );
}
